import React, { useRef, useState } from "react";

import { TFunction, useGetCropMixComposerScoreFactors, useTranslation } from "utils";

import { CommercialMixOverviewTableDataType, CropMixComposerCommercialMix, CropsOverviewTableColumnConfig } from "models";

import { Divider, Flex, IconLinkButton, OverflowEllipsisWithTooltip, Popover, Table, Tag, Typography } from "components";

import { useCropMixComposerContext } from "pages/app/crop-mix-composer/CropMixComposerContextProvider.tsx";
import { TopMatchCard } from "pages/app/crop-mix-composer/components/commercialMix/TopMatchCard.tsx";
import { createTopMatchCardProps } from "pages/app/crop-mix-composer/utils/commercialMixUtils";

export const CommercialMixTable = ({ cropMixComposerCommercialMix }: { cropMixComposerCommercialMix: CropMixComposerCommercialMix[] }) => {
  const { t } = useTranslation(["cropMixComposer", "practices"]);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { selectedGoals } = useCropMixComposerContext();
  const { data: scoreFactors } = useGetCropMixComposerScoreFactors();
  const [pageSize, setPageSize] = useState(50);
  const [page, setPage] = useState(1);

  const cropsOverviewTableColumnsConfigs: CropsOverviewTableColumnConfig[] = [
    {
      title: <Typography variant="paragraph-s">{t("cropMixComposer:commercialCropMixes.table.name")}</Typography>,
      dataIndex: "mixName",
      key: "mixName",
    },
    {
      title: <Typography variant="paragraph-s">{t("cropMixComposer:commercialCropMixes.table.provider")}</Typography>,
      dataIndex: "provider",
      key: "provider",
    },
    {
      title: <Typography variant="paragraph-s">{t("cropMixComposer:commercialCropMixes.table.matchScore")}</Typography>,
      dataIndex: "matchScoreAndDetails",
      key: "matchScoreAndDetails",
      width: "40%",
    },
  ];

  const getDetails = (commercialMix: CropMixComposerCommercialMix, t: TFunction) => {
    // Create TopMatchCard props with goal scores
    const topMatchCardProps = scoreFactors ? createTopMatchCardProps(commercialMix, t, selectedGoals, scoreFactors) : null;

    return (
      <Flex justify="flex-end" align="center">
        <Popover
          dataTestId="commercial-mix-top-crop-card-popover"
          className="top-crop-card-popover"
          placement="leftTop"
          arrow={false}
          trigger="click"
          content={topMatchCardProps ? <TopMatchCard commercialMix={topMatchCardProps} isPopover={true} /> : <div>Loading...</div>}
        >
          <Typography variant="paragraph-m" className="details">
            {t("cropMixComposer:commercialCropMixes.table.details")}{" "}
          </Typography>
        </Popover>
        <Flex className="match-score-divider" align="center">
          <Divider type="vertical" />
          <IconLinkButton
            disabled={commercialMix.link === null}
            iconType="logout"
            text={t("cropMixComposer:topMatchCard.contactProvider")}
            onClick={() => window.open(commercialMix.link!, "_blank", "noopener,noreferrer")}
          />
        </Flex>
      </Flex>
    );
  };

  const tableData: CommercialMixOverviewTableDataType[] = cropMixComposerCommercialMix.map((commercialMix) => ({
    key: commercialMix.id,
    mixName: (
      <Flex align="center">
        <OverflowEllipsisWithTooltip title={commercialMix.name} className="mix-name">
          {commercialMix.name}
        </OverflowEllipsisWithTooltip>
      </Flex>
    ),
    provider: (
      <Flex align="center">
        <OverflowEllipsisWithTooltip title={commercialMix.name} className="provider">
          {`${commercialMix.provider} ©`}
        </OverflowEllipsisWithTooltip>
      </Flex>
    ),
    matchScoreAndDetails: (
      <Flex align="center" justify="space-between">
        <Tag className="tag" colour="yellow" text={t("cropMixComposer:topMatchCard.matchScore", { matchScore: commercialMix.matchScore })} />
        {getDetails(commercialMix, t)}
      </Flex>
    ),
  }));

  return (
    <div className="crop-mix-composer-overlay-step-overview" ref={tableContainerRef}>
      <Table
        className="commercial-crop-mixes-table"
        data-testid="commercial-crop-mixes-table"
        columns={cropsOverviewTableColumnsConfigs}
        data={tableData}
        pagination={{
          total: cropMixComposerCommercialMix?.length,
          defaultPageSize: 50,
          pageSize: pageSize,
          current: page,
          onChange: (newPage, newLimit) => {
            if (newLimit !== pageSize) {
              setPageSize(newLimit);
              setPage(1);
            } else {
              setPageSize(pageSize);
              setPage(newPage);
            }
          },
        }}
        setEqualWidthColumns={false}
        sticky={true}
        scroll={{
          y: `${Math.max(window.innerHeight * 0.6, window.innerHeight - (tableContainerRef.current?.offsetTop || 0))}px`,
        }}
      />
    </div>
  );
};
